import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'

const BlogPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('All')
    const [newsletterEmail, setNewsletterEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [message, setMessage] = useState('')
    const [posts, setPosts] = useState([])
    const [loading, setLoading] = useState(true)
    const [categories, setCategories] = useState(['All'])

    useEffect(() => {
        fetchPosts()
    }, [])

    async function fetchPosts() {
        try {
            const apiUrl = '/api/blog/posts?limit=50'
            
            const response = await fetch(apiUrl)
            const data = await response.json()
            setPosts(data.posts || [])
            
            // Extract unique categories
            const uniqueCategories = ['All', ...new Set((data.posts || []).map(post => post.category))]
            setCategories(uniqueCategories)
        } catch (error) {
            console.error('Error loading posts:', error)
        } finally {
            setLoading(false)
        }
    }
    
    // Filter posts by category
    const filteredPosts = selectedCategory === 'All' 
        ? posts 
        : posts.filter(post => post.category === selectedCategory)

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault()
        if (!newsletterEmail) return

        setIsSubmitting(true)
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: newsletterEmail })
            })

            const data = await response.json()

            if (response.ok) {
                setMessage('Thank you for subscribing!')
                setNewsletterEmail('')
                
                // Track Meta Pixel event
                if (typeof window !== 'undefined' && window.fbq) {
                    window.fbq('track', 'Subscribe', {
                        content_name: 'Newsletter Subscription',
                        value: 1.00,
                        currency: 'ZAR'
                    });
                }
            } else {
                setMessage(data.error || 'Something went wrong. Please try again.')
            }
        } catch (error) {
            console.error('Newsletter subscription error:', error)
            setMessage('Failed to subscribe. Please try again later.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div>
            <PageMeta 
                title="Web Design & Digital Marketing Blog | RDS Studio Centurion"
                description="Expert guides on web design, WordPress, SEO & digital marketing from Centurion's RDS Studio. Practical tips that help Gauteng businesses grow online."
                canonical="https://rdsstudio.co.za/blog"
            />
            <Header />
            <main className="main-content">
                <section className="section blog">
                    <div className="section-inner">
                        <h1 className="title title-h2">Insights &<br/>Articles</h1>
                        <p className="description">
                            Helpful articles on web development, digital marketing, video production, and growing your online presence. 
                            Practical advice based on real experience.
                        </p>

                        {/* Category Filter */}
                        <div className="blog-categories">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        {/* Blog Grid */}
                        {loading ? (
                            <div className="loading-message">Loading articles...</div>
                        ) : (
                            <div className="blog-grid">
                                {filteredPosts.map((post) => (
                                    <article key={post._id} className="blog-card">
                                        {post.featuredImage && (
                                            <div className="blog-card-image">
                                                <Link to={`/blog/${post.slug}`}>
                                                    <img 
                                                        src={post.featuredImage}
                                                        alt={post.title}
                                                        loading="lazy"
                                                    />
                                                </Link>
                                            </div>
                                        )}
                                        <div className="blog-card-content">
                                            <span className="blog-card-category">{post.category}</span>
                                            <h2 className="blog-card-title">
                                                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                                            </h2>
                                            <p className="blog-card-excerpt">{post.excerpt}</p>
                                            <div className="blog-card-footer">
                                                <span className="blog-card-date">{new Date(post.publishDate || post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                <Link to={`/blog/${post.slug}`} className="blog-card-link">
                                                    Read More <i className="fas fa-arrow-right"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}

                        {filteredPosts.length === 0 && (
                            <div className="no-posts">
                                <p>No articles found in this category yet.</p>
                            </div>
                        )}

                        {/* Newsletter Signup */}
                        <div className="blog-newsletter">
                            <h2>Stay Updated</h2>
                            <p>Subscribe to get notified when new articles are published.</p>
                            <form className="newsletter-form-inline" onSubmit={handleNewsletterSubmit}>
                                <input 
                                    type="email" 
                                    placeholder="Enter your email" 
                                    required 
                                    value={newsletterEmail}
                                    onChange={(e) => setNewsletterEmail(e.target.value)}
                                    disabled={isSubmitting}
                                />
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                                </button>
                            </form>
                            {message && (
                                <p className={`newsletter-message ${message.includes('Thank') ? 'success' : 'error'}`} style={{ marginTop: 'var(--space-3)' }}>
                                    {message}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export default BlogPage
